/**
 * Client Care Tab
 * 
 * Shows the Care Plan and authorized services.
 */
import React from "react";
import { Link } from "react-router-dom";
import { 
  Heart, Check, Clock, AlertCircle, FileText, Sparkles,
  ExternalLink, ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";

// Service category colors
const CATEGORY_COLORS = {
  adl: "bg-blue-100 text-blue-800 border-blue-200",
  iadl: "bg-purple-100 text-purple-800 border-purple-200",
  supervision: "bg-amber-100 text-amber-800 border-amber-200",
  skilled: "bg-green-100 text-green-800 border-green-200",
};

export default function ClientCareTab({ client, carePlan, onUpdate }) {
  if (!client) return null;

  const hasCarePlan = carePlan && carePlan.services && carePlan.services.length > 0;
  
  // Group services by category
  const servicesByCategory = {};
  if (hasCarePlan) {
    carePlan.services.forEach(service => {
      const category = service.category || "other";
      if (!servicesByCategory[category]) {
        servicesByCategory[category] = [];
      }
      servicesByCategory[category].push(service);
    });
  }

  return (
    <div className="space-y-6" data-testid="care-tab">
      {/* Care Plan Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Care Plan
              </CardTitle>
              <CardDescription className="mt-1">
                {hasCarePlan 
                  ? `Approved on ${new Date(carePlan.approved_at || carePlan.created_at).toLocaleDateString()}`
                  : "No care plan on file"
                }
              </CardDescription>
            </div>
            {client.assessment_id && (
              <Link to={`/assessments/${client.assessment_id}/care-plan`}>
                <Button variant="outline" size="sm">
                  <FileText className="w-4 h-4 mr-2" />
                  View Full Plan
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
              </Link>
            )}
          </div>
        </CardHeader>
        
        {hasCarePlan && (
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {carePlan.total_authorized_hours_weekly || carePlan.services.length * 2}
                </p>
                <p className="text-xs text-muted-foreground">Hours/Week</p>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">
                  {carePlan.services.length}
                </p>
                <p className="text-xs text-muted-foreground">Services</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">
                  {carePlan.visit_frequency || "Weekly"}
                </p>
                <p className="text-xs text-muted-foreground">Frequency</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">
                  {carePlan.effective_date 
                    ? new Date(carePlan.effective_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                    : "N/A"
                  }
                </p>
                <p className="text-xs text-muted-foreground">Effective</p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Authorized Services */}
      {hasCarePlan ? (
        <div className="space-y-4">
          {Object.entries(servicesByCategory).map(([category, services]) => (
            <Card key={category}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Badge 
                    variant="outline" 
                    className={`capitalize ${CATEGORY_COLORS[category] || "bg-gray-100"}`}
                  >
                    {category === "adl" ? "ADL - Activities of Daily Living" : 
                     category === "iadl" ? "IADL - Instrumental Activities" :
                     category.replace(/_/g, " ")}
                  </Badge>
                  <span className="text-muted-foreground text-sm font-normal">
                    ({services.length} services)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2">
                  {services.map((service, idx) => (
                    <div 
                      key={service.service_key || idx}
                      className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100"
                    >
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">
                          {service.service_name || service.service_key}
                        </p>
                        {service.frequency && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {service.frequency}
                          </p>
                        )}
                        {service.notes && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {service.notes}
                          </p>
                        )}
                        {service.ai_reasoning && (
                          <p className="text-xs text-purple-600 mt-1 italic">
                            <Sparkles className="w-3 h-3 inline mr-1" />
                            {service.ai_reasoning}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Heart className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground mb-4">
              No care plan on file yet
            </p>
            {client.assessment_id && (
              <Link to={`/assessments/${client.assessment_id}/care-plan`}>
                <Button>
                  <Sparkles className="w-4 h-4 mr-2" />
                  View/Generate Care Plan
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {/* Caregiver Requirements */}
      {carePlan && carePlan.caregiver_requirements && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Caregiver Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {carePlan.caregiver_requirements.required_skills?.map(skill => (
                <Badge key={skill} variant="outline" className="capitalize">
                  {skill.replace(/_/g, " ")}
                </Badge>
              ))}
              {carePlan.caregiver_requirements.gender_preference && (
                <Badge variant="secondary">
                  {carePlan.caregiver_requirements.gender_preference} Preferred
                </Badge>
              )}
              {carePlan.caregiver_requirements.language_requirement && (
                <Badge variant="secondary">
                  Speaks {carePlan.caregiver_requirements.language_requirement}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Safety Considerations */}
      {carePlan && carePlan.safety_considerations && carePlan.safety_considerations.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Safety Considerations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {carePlan.safety_considerations.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
